import { FC, useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthDispatchContext } from 'providers/Auth';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { PASSWORD_PATTERN, isMobileOrTablet } from '@medicusunion/front-utils';

import { Content, Linkwrapper } from './styled';
import { MessageChangePassword } from '../components';

const { Title } = Typography;

const NewPassword: FC = () => {
  // translation
  const { t } = useTranslation();
  // navigation
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // form
  const [form] = Form.useForm();
  // context
  const { setPassword } = useAuthDispatchContext();
  // state
  const [isLoading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const key = searchParams.get('key');
        const checkDeepLinkParam = searchParams.get('checkDeepLink') !== '0';

        if (!key) {
          throw new Error();
        }

        const checkDeepLink = isMobileOrTablet();

        if (checkDeepLink && checkDeepLinkParam) {
          setTimeout(() => {
            window.location.href = window.location.href + '&checkDeepLink=0';
          }, 25);
          window.location.href = `medicusdoctor://new-password?key=${key}`;
        }
      } catch (_) {
        navigate('/login');
      }
    })();
  }, [navigate, searchParams]);

  const onFinish = useCallback(
    values => {
      setLoading(true);
      const key = searchParams.get('key') as string;
      setPassword(values.password, key)
        .then(() => {
          setSuccessMessage(true);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [searchParams, setPassword]
  );

  return (
    <>
      {successMessage ? (
        <MessageChangePassword />
      ) : (
        <Content>
          <Title level={2} style={{ marginBottom: 24 }}>
            {t('auth.new_password')}
          </Title>
          <Form form={form} layout="vertical" size="large" onFinish={onFinish}>
            <Form.Item
              name="password"
              messageVariables={{
                label: t('auth.new_password_placeholder')
              }}
              rules={[
                { required: true },
                {
                  pattern: PASSWORD_PATTERN
                }
              ]}
            >
              <Input.Password
                placeholder={t('auth.new_password_placeholder')}
              />
            </Form.Item>
            <Form.Item
              name="confirm_password"
              messageVariables={{
                label: t('auth.confirm_password_placeholder')
              }}
              rules={[
                { required: true, message: t('auth.passwords_not_match') },
                {
                  message: t('auth.passwords_not_match'),
                  validator: (_, value) => {
                    if (value === form.getFieldValue('password')) {
                      return Promise.resolve();
                    }

                    return Promise.reject();
                  }
                }
              ]}
            >
              <Input.Password
                placeholder={t('auth.confirm_password_placeholder')}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                shape="round"
                loading={isLoading}
                block
              >
                {t('auth.reset_password')}
              </Button>
            </Form.Item>
            <Linkwrapper>
              <Link
                to="/login"
                style={{ fontSize: 12, color: 'var(--text-color)' }}
              >
                <ArrowLeftOutlined style={{ marginRight: 12 }} />
                {t('auth.back_login')}
              </Link>
            </Linkwrapper>
          </Form>
        </Content>
      )}
    </>
  );
};

export default NewPassword;
